import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Attachment, ErrorCode } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import { useAttachmentDelete, useAttachmentUpload } from '../api/queries.js';
import { formatBytes } from '../lib/format.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import styles from './IssueAttachments.module.css';

/**
 * Вложения задачи.
 *
 * Файл уходит прямо в Jira и появляется в задаче для всех, кто её откроет, — своей копии Jisy
 * не держит. Отсюда и подтверждение на удаление: оно необратимо и видно всей команде.
 *
 * Файлы кладутся по одному, даже когда их выбрали несколько: Jira принимает по файлу за запрос,
 * а последовательная отправка позволяет сказать, на каком именно всё остановилось.
 */
export function IssueAttachments({
  issueKey,
  attachments,
}: {
  issueKey: string;
  attachments: Attachment[];
}): React.ReactElement {
  const { t } = useTranslation();

  const upload = useAttachmentUpload(issueKey);
  const remove = useAttachmentDelete(issueKey);

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [failure, setFailure] = useState<ApiRequestError | null>(null);
  const [confirming, setConfirming] = useState<Attachment | null>(null);

  async function send(files: File[]): Promise<void> {
    setFailure(null);

    for (const file of files) {
      try {
        await upload.mutateAsync(file);
      } catch (error) {
        // Дальше не идём: чаще всего причина общая — нет прав или Jira не отвечает.
        setFailure(error instanceof ApiRequestError ? error : null);
        break;
      }
    }
  }

  const removeFailed =
    remove.error instanceof ApiRequestError ? remove.error.code : remove.error ? 'INTERNAL' : null;

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <span className="label">{t('issue.attachments', { count: attachments.length })}</span>
        <button
          type="button"
          className={styles.linkButton}
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isPending ? t('issue.attachmentUploading') : t('issue.attachmentAdd')}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="visually-hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          // Значение сбрасывается сразу: иначе повторный выбор того же файла не даст события.
          event.target.value = '';
          if (files.length > 0) void send(files);
        }}
      />

      <div
        className={`${styles.dropZone} ${dragging ? styles.dropZoneActive : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const files = Array.from(event.dataTransfer.files);
          if (files.length > 0) void send(files);
        }}
      >
        {attachments.length === 0 ? (
          <p className={styles.empty}>{t('issue.attachmentsEmpty')}</p>
        ) : (
          <ul className={styles.list}>
            {attachments.map((file) => (
              <li className={styles.item} key={file.id}>
                <a
                  className={styles.file}
                  href={file.contentUrl}
                  download={file.filename}
                  title={file.filename}
                >
                  {file.thumbnailUrl ? (
                    <img className={styles.thumb} src={file.thumbnailUrl} alt="" loading="lazy" />
                  ) : (
                    <span className={`mono ${styles.glyph}`} aria-hidden="true">
                      {extensionOf(file.filename)}
                    </span>
                  )}
                  <span className={styles.meta}>
                    <span className={`truncate ${styles.name}`}>{file.filename}</span>
                    <span className={`mono ${styles.size}`}>{formatBytes(file.size)}</span>
                  </span>
                </a>
                <button
                  type="button"
                  className={styles.removeButton}
                  aria-label={t('issue.attachmentRemove', { name: file.filename })}
                  onClick={() => {
                    remove.reset();
                    setConfirming(file);
                  }}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {failure && (
        <p className={styles.error} role="alert">
          {t(`errors.${failure.code}` satisfies `errors.${ErrorCode}`, failure.params ?? {})}
        </p>
      )}

      {confirming && (
        <ConfirmDialog
          title={t('issue.attachmentRemoveTitle', { name: confirming.filename })}
          body={t('issue.attachmentRemoveBody')}
          confirmLabel={t('issue.attachmentRemoveConfirm')}
          busy={remove.isPending}
          error={removeFailed}
          onCancel={() => {
            remove.reset();
            setConfirming(null);
          }}
          onConfirm={() =>
            remove.mutate(confirming.id, { onSuccess: () => setConfirming(null) })
          }
        />
      )}
    </section>
  );
}

/** Значок файла без миниатюры — его расширение: по нему тип узнаётся быстрее, чем по имени. */
function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return '·';
  return filename.slice(dot + 1, dot + 5).toUpperCase();
}
